import { useEndImpersonationMutation } from '@features/users/usersApi';
import { SettingsContext } from '@contexts/SettingsContext';
import { apiPath } from '@helpers/pathHelper';
import { Box, Menu, MenuItem, MenuProps } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ComponentProps, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';

type SxProps = ComponentProps<typeof Box>['sx'];

type MenuDropdownProps = {
  sx: SxProps;
  anchorEl: null | HTMLElement;
  onClose: () => void;
  transformOrigin: MenuProps['transformOrigin'];
  anchorOrigin: MenuProps['anchorOrigin'];
  onHelpModalToggle?: () => void;
};

export const MenuDropdown = (props: MenuDropdownProps) => {
  const navigate = useNavigate();
  const { hubMode, admin, impersonated } = useContext(SettingsContext);
  const [endImpersonation] = useEndImpersonationMutation();

  const menuStyles: SxProps = {
    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: 1.5,
    '&:before': { bgcolor: 'background.paper' },
  };

  const open = Boolean(props.anchorEl);
  const handleClose = () => {
    props.onClose();
  };
  const { enqueueSnackbar } = useSnackbar();

  const onEndImpersonation = async () => {
    try {
      await endImpersonation().unwrap();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to end impersonation. Try reloading the page.', {
        variant: 'error',
        autoHideDuration: null,
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        preventDuplicate: true,
      });
      return;
    }

    void navigate('/users');
    location.reload();
  };

  return (
    <Menu
      anchorEl={props.anchorEl}
      id="account-menu"
      className="nd-nav-dropdown"
      open={open}
      onClose={handleClose}
      onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: menuStyles,
      }}
      transformOrigin={props.transformOrigin}
      anchorOrigin={props.anchorOrigin}
    >
      {admin ? (
        <Link to="/admin/stats">
          <MenuItem>Report Data Approval</MenuItem>
        </Link>
      ) : null}
      {!admin && (
        // Not using Link because this is a download.
        <a href={apiPath('/flat_reports.csv')} target="_blank" rel="noreferrer">
          <MenuItem>Flat Reports</MenuItem>
        </a>
      )}
      {hubMode
        ? [
            <MenuItem
              key="help"
              onClick={e => {
                e.preventDefault();
                props?.onHelpModalToggle?.();
              }}
            >
              Help
            </MenuItem>,
          ]
        : null}
      {admin
        ? [
            <Link to="/users" key="users">
              <MenuItem>Users</MenuItem>
            </Link>,
            <Link to="/automated-deliveries" key="deliveries">
              <MenuItem>Automated Deliveries</MenuItem>
            </Link>,
            <Link to="/usage" key="usage">
              <MenuItem>Usage</MenuItem>
            </Link>,
            <Link to="/feature-flags" key="feature-flags">
              <MenuItem>Feature Flags</MenuItem>
            </Link>,
          ]
        : null}
      {/* Redirects for logout and impersonation fail when implemented using react link router, so it is left as an html link. */}
      {!impersonated && (
        <a href={apiPath('/user_sessions/logout')}>
          <MenuItem>Logout</MenuItem>
        </a>
      )}
      {impersonated ? (
        <MenuItem onClick={() => void onEndImpersonation()}>
          End Impersonation
        </MenuItem>
      ) : null}
    </Menu>
  );
};
